/**
 * Admin · Smart building — zones, devices, activity, integrations.
 *
 * Software-first, hardware-ready. The building becomes part of the
 * communication system — never a tracker.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Pencil, Building2, Tablet, Monitor,
  DoorOpen, RadioTower, Activity, Copy, Sparkles, Download,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";

const ZONE_KINDS = [
  { value: "yard", label: "Yard", color: "#8A9984" },
  { value: "playroom", label: "Playroom", color: "#FFB800" },
  { value: "classroom", label: "Classroom", color: "#1F3A2A" },
  { value: "lunch_area", label: "Lunch area", color: "#E6BFA6" },
  { value: "rest_area", label: "Rest area", color: "#A89CC8" },
  { value: "reception", label: "Reception", color: "#9BA995" },
  { value: "in_care", label: "In Care", color: "#1A1B2E" },
  { value: "indoor", label: "Indoor" },
  { value: "outdoor", label: "Outdoor" },
  { value: "quiet", label: "Quiet" },
  { value: "transitional", label: "Transitional" },
  { value: "shared", label: "Shared" },
];

const ZONE_KIND_META = Object.fromEntries(ZONE_KINDS.map((k) => [k.value, k]));

const DEVICE_KINDS = [
  { value: "tablet", label: "Staff tablet", icon: Tablet },
  { value: "wall_panel", label: "Wall panel", icon: Monitor },
  { value: "kiosk", label: "Child kiosk", icon: Sparkles },
  { value: "signage", label: "Digital signage", icon: Monitor },
  { value: "access_control", label: "Access control", icon: DoorOpen },
  { value: "sensor", label: "Sensor", icon: RadioTower },
];

const DEVICE_KIND_META = Object.fromEntries(DEVICE_KINDS.map((k) => [k.value, k]));

function fmtRelative(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return d.toLocaleDateString();
}

export default function AdminSmartBuilding() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [tab, setTab] = useState("zones");
  const [zones, setZones] = useState([]);
  const [devices, setDevices] = useState([]);
  const [usage, setUsage] = useState([]);
  const [activity, setActivity] = useState([]);
  const [integrations, setIntegrations] = useState({});
  const [composer, setComposer] = useState(null); // {type, mode, initial}

  const reload = async () => {
    if (!workspace) return;
    try {
      const [zr, dr, ur, ar, ac, sr, sg] = await Promise.all([
        api.get(`/workspaces/${workspace.id}/zones`),
        api.get(`/admin/workspaces/${workspace.id}/devices`),
        api.get(`/admin/workspaces/${workspace.id}/zones/usage`),
        api.get(`/admin/workspaces/${workspace.id}/zones/activity?days=7&limit=200`),
        api.get(`/admin/workspaces/${workspace.id}/access-control/status`),
        api.get(`/admin/workspaces/${workspace.id}/sensors/status`),
        api.get(`/admin/workspaces/${workspace.id}/signage/status`),
      ]);
      setZones(zr.data || []);
      setDevices(dr.data || []);
      setUsage(ur.data || []);
      setActivity(ar.data || []);
      setIntegrations({ accessControl: ac.data, sensors: sr.data, signage: sg.data });
    } catch {
      // quiet
    }
  };

  useEffect(() => { reload(); }, [workspace?.id]);

  const downloadActivityCsv = async () => {
    try {
      const res = await api.get(
        `/admin/workspaces/${workspace.id}/zones/activity.csv?days=30`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `alouvya-zone-activity.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* quiet */ }
  };

  if (wsLoading) return null;
  if (!workspace) {
    return (
      <AdminEmpty testid="admin-smart-building-no-ws">
        No workspace yet.
      </AdminEmpty>
    );
  }

  return (
    <div data-testid="admin-smart-building-screen">
      <AdminHeader
        eyebrow="Smart building"
        title="A calm hub, room by room"
      />
      <p className="mt-2 text-sm text-[#525252] max-w-2xl">
        The building never tracks anyone. It offers calm touchpoints — wall
        panels, tablets, signage — that help staff, children, and parents
        coordinate. Software-first, hardware-ready.
      </p>

      {/* Tabs */}
      <div className="mt-6 inline-flex gap-1 bg-[#F2EFEA] p-1 rounded-full">
        {[
          { v: "zones", label: "Zones" },
          { v: "devices", label: "Devices" },
          { v: "activity", label: "Activity" },
          { v: "integrations", label: "Integrations" },
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setTab(t.v)}
            data-testid={`admin-smart-tab-${t.v}`}
            className={
              "px-4 py-2 rounded-full text-xs tracking-[0.16em] uppercase font-medium transition-colors " +
              (tab === t.v
                ? "bg-[#132619] text-white"
                : "text-[#525252] hover:bg-white")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "zones" && (
        <ZonesTab
          zones={zones}
          usage={usage}
          workspaceId={workspace.id}
          onCompose={() => setComposer({ type: "zone", mode: "create" })}
          onEdit={(z) => setComposer({ type: "zone", mode: "edit", initial: z })}
          onReload={reload}
        />
      )}

      {tab === "devices" && (
        <DevicesTab
          devices={devices}
          zones={zones}
          workspaceId={workspace.id}
          onCompose={() => setComposer({ type: "device", mode: "create" })}
          onEdit={(d) => setComposer({ type: "device", mode: "edit", initial: d })}
          onReload={reload}
        />
      )}

      {tab === "activity" && (
        <ActivityTab activity={activity} onDownload={downloadActivityCsv} />
      )}

      {tab === "integrations" && (
        <IntegrationsTab integrations={integrations} />
      )}

      {composer && (
        <Composer
          spec={composer}
          workspaceId={workspace.id}
          zones={zones}
          onCancel={() => setComposer(null)}
          onSaved={() => { setComposer(null); reload(); }}
        />
      )}
    </div>
  );
}

// -------- Zones tab --------
function ZonesTab({ zones, usage, workspaceId, onCompose, onEdit, onReload }) {
  const usageById = useMemo(
    () => Object.fromEntries(usage.map((u) => [u.zone_id, u])),
    [usage]
  );
  const remove = async (z) => {
    if (!confirm(`Remove "${z.name}"?`)) return;
    try {
      await api.delete(`/workspaces/${workspaceId}/zones/${z.id}`);
      toast("Zone calmly removed.");
      onReload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove the zone.");
    }
  };
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#525252]">
          {zones.length} zone{zones.length === 1 ? "" : "s"} configured.
        </p>
        <button
          type="button"
          onClick={onCompose}
          data-testid="admin-smart-zones-add"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117] transition-colors"
        >
          <Plus size={13} strokeWidth={1.8} />
          New zone
        </button>
      </div>
      {zones.length === 0 ? (
        <AdminEmpty testid="admin-smart-zones-empty">
          No zones yet. Add Yard, Playroom, Classroom, Lunch, Rest, Reception, In Care…
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-smart-zones-list"
          className="mt-4 grid sm:grid-cols-2 gap-3"
        >
          {zones.map((z) => {
            const meta = ZONE_KIND_META[z.kind] || {};
            const u = usageById[z.id] || {};
            return (
              <li
                key={z.id}
                data-testid={`admin-smart-zone-row-${z.id}`}
                className="rounded-3xl bg-white border hairline p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span
                      aria-hidden
                      className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0"
                      style={{ background: `${meta.color || z.color}20` }}
                    >
                      <Building2
                        size={14}
                        strokeWidth={1.6}
                        style={{ color: meta.color || z.color }}
                      />
                    </span>
                    <div className="min-w-0">
                      <div className="font-serif text-base text-[#171717]">
                        {z.name}
                      </div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-[#525252] mt-0.5">
                        {meta.label || z.kind}
                        {z.assigned_group_ids?.length > 0 &&
                          ` · ${z.assigned_group_ids.join(" · ")}`}
                      </div>
                      <div className="text-xs text-[#525252] mt-2">
                        {u.current_count ?? 0} child{(u.current_count ?? 0) === 1 ? "" : "ren"} here now
                        {z.capacity ? ` · capacity ${z.capacity}` : ""}
                      </div>
                      {u.sample_child_names?.length > 0 && (
                        <div className="text-[10px] tracking-[0.18em] uppercase text-[#8A9984] mt-1.5">
                          {u.sample_child_names.slice(0, 5).join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => onEdit(z)}
                      data-testid={`admin-smart-zone-edit-${z.id}`}
                      className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#EAE5EF] flex items-center justify-center text-[#525252]"
                      aria-label="Edit"
                    >
                      <Pencil size={12} strokeWidth={1.6} />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(z)}
                      data-testid={`admin-smart-zone-delete-${z.id}`}
                      className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#FFB800]/30 flex items-center justify-center text-[#525252]"
                      aria-label="Delete"
                    >
                      <Trash2 size={12} strokeWidth={1.6} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// -------- Devices tab --------
function DevicesTab({ devices, zones, workspaceId, onCompose, onEdit, onReload }) {
  const remove = async (d) => {
    if (!confirm(`Remove "${d.label}"?`)) return;
    try {
      await api.delete(`/admin/workspaces/${workspaceId}/devices/${d.id}`);
      toast("Device gently retired.");
      onReload();
    } catch {
      toast("Couldn't remove the device.");
    }
  };
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#525252]">
          {devices.length} device{devices.length === 1 ? "" : "s"} paired.
        </p>
        <button
          type="button"
          onClick={onCompose}
          data-testid="admin-smart-devices-add"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117] transition-colors"
        >
          <Plus size={13} strokeWidth={1.8} />
          New device
        </button>
      </div>
      {devices.length === 0 ? (
        <AdminEmpty testid="admin-smart-devices-empty">
          No devices yet. Pair a staff tablet, wall panel, or signage screen.
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-smart-devices-list"
          className="mt-4 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {devices.map((d) => {
            const meta = DEVICE_KIND_META[d.kind] || DEVICE_KIND_META.tablet;
            const Icon = meta.icon;
            return (
              <li
                key={d.id}
                data-testid={`admin-smart-device-row-${d.id}`}
                className="px-5 py-4 flex items-start gap-4"
              >
                <span
                  aria-hidden
                  className="h-10 w-10 rounded-2xl bg-[#F2EFEA] flex items-center justify-center shrink-0"
                >
                  <Icon size={14} strokeWidth={1.6} className="text-[#1A1B2E]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-base text-[#171717]">
                    {d.label}
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#525252] mt-0.5">
                    {meta.label}
                    {d.zone_name && ` · ${d.zone_name}`}
                    {d.group_id && ` · ${d.group_id}`}
                  </div>
                  <div className="text-xs text-[#525252] mt-1.5 flex flex-wrap gap-3">
                    <span>Last seen · {fmtRelative(d.last_seen_at)}</span>
                    {d.note && <span className="italic">"{d.note}"</span>}
                  </div>
                </div>
                <div className="shrink-0 flex gap-1">
                  <button
                    type="button"
                    onClick={() => onEdit(d)}
                    data-testid={`admin-smart-device-edit-${d.id}`}
                    className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#EAE5EF] flex items-center justify-center text-[#525252]"
                    aria-label="Edit"
                  >
                    <Pencil size={12} strokeWidth={1.6} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(d)}
                    data-testid={`admin-smart-device-delete-${d.id}`}
                    className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#FFB800]/30 flex items-center justify-center text-[#525252]"
                    aria-label="Delete"
                  >
                    <Trash2 size={12} strokeWidth={1.6} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// -------- Activity tab --------
function ActivityTab({ activity, onDownload }) {
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#525252]">
          {activity.length} zone event{activity.length === 1 ? "" : "s"} in the past 7 days.
        </p>
        <button
          type="button"
          onClick={onDownload}
          data-testid="admin-smart-activity-download-csv"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border hairline text-xs tracking-[0.16em] uppercase font-medium text-[#525252] hover:bg-[#F2EFEA] transition-colors"
        >
          <Download size={13} strokeWidth={1.7} />
          Download CSV
        </button>
      </div>
      {activity.length === 0 ? (
        <AdminEmpty testid="admin-smart-activity-empty">
          No zone events yet. They appear here as children move calmly between zones.
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-smart-activity-list"
          className="mt-4 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {activity.slice(0, 100).map((ev) => (
            <li
              key={ev.id}
              data-testid={`admin-smart-activity-row-${ev.id}`}
              className="px-5 py-3 flex items-center gap-3"
            >
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{
                  background: ev.event === "enter" ? "#1A1B2E" : "#A89CC8",
                }}
              />
              <div className="flex-1 min-w-0 text-sm text-[#2C2B29]">
                <span className="font-medium">{ev.child_name}</span>
                <span className="text-[#525252]"> · </span>
                <span className="text-[#525252]">
                  {ev.event === "enter" ? "entered" : "left"}
                </span>
                <span className="text-[#525252]"> </span>
                <span className="font-medium">{ev.zone_name}</span>
                {ev.by_user_name && (
                  <span className="text-[#9CA3AF]"> · by {ev.by_user_name}</span>
                )}
              </div>
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252] shrink-0">
                {fmtRelative(ev.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -------- Integrations tab --------
function IntegrationsTab({ integrations }) {
  const blocks = [
    { key: "accessControl", label: "Access control", icon: DoorOpen, copy: integrations.accessControl?.message },
    { key: "sensors", label: "Building sensors", icon: RadioTower, copy: integrations.sensors?.message },
    { key: "signage", label: "Digital signage", icon: Monitor, copy: integrations.signage?.message },
  ];
  return (
    <ul
      data-testid="admin-smart-integrations"
      className="mt-6 space-y-3"
    >
      {blocks.map(({ key, label, icon: Icon, copy }) => {
        const cfg = integrations[key] || {};
        return (
          <li
            key={key}
            data-testid={`admin-smart-integration-${key}`}
            className="rounded-3xl bg-white border hairline p-5 flex items-start gap-4"
          >
            <span
              aria-hidden
              className="h-11 w-11 rounded-2xl bg-[#F2EFEA] flex items-center justify-center shrink-0"
            >
              <Icon size={15} strokeWidth={1.6} className="text-[#1A1B2E]" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-base text-[#171717]">
                  {label}
                </span>
                <span
                  className={
                    "text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full " +
                    (cfg.configured
                      ? "bg-[#132619]/15 text-[#1A1B2E]"
                      : "bg-[#A89CC8]/15 text-[#1F3A2A]")
                  }
                >
                  {cfg.configured ? "Configured" : "Coming soon"}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-[#525252]">{copy || "—"}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// -------- Composer (zone OR device) --------
function Composer({ spec, workspaceId, zones, onCancel, onSaved }) {
  const isZone = spec.type === "zone";
  const isEdit = spec.mode === "edit";
  const init = spec.initial || {};
  const [form, setForm] = useState(
    isZone
      ? {
          name: init.name || "",
          kind: init.kind || "yard",
          color: init.color || "#9BA995",
          capacity: init.capacity || "",
          assigned_group_ids: (init.assigned_group_ids || []).join(", "),
        }
      : {
          label: init.label || "",
          kind: init.kind || "tablet",
          zone_id: init.zone_id || "",
          group_id: init.group_id || "",
          note: init.note || "",
        }
  );
  const [saving, setSaving] = useState(false);
  const [pairToken, setPairToken] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isZone) {
        const body = {
          name: form.name.trim(),
          kind: form.kind,
          color: form.color,
          capacity: form.capacity ? parseInt(form.capacity, 10) : null,
          assigned_group_ids: form.assigned_group_ids
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        };
        if (isEdit) {
          await api.patch(`/workspaces/${workspaceId}/zones/${init.id}`, body);
        } else {
          await api.post(`/workspaces/${workspaceId}/zones`, body);
        }
        toast("Zone calmly saved.");
        onSaved();
      } else {
        const body = {
          label: form.label.trim(),
          kind: form.kind,
          zone_id: form.zone_id || null,
          group_id: form.group_id.trim() || null,
          note: form.note.trim() || null,
        };
        if (isEdit) {
          await api.patch(
            `/admin/workspaces/${workspaceId}/devices/${init.id}`,
            body
          );
          toast("Device gently updated.");
          onSaved();
        } else {
          const r = await api.post(
            `/admin/workspaces/${workspaceId}/devices`,
            body
          );
          // Display pair token (one-shot) so admin can enroll the device.
          setPairToken(r.data.pair_token);
        }
      }
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(pairToken);
      toast("Pair token copied.");
    } catch {
      toast("Copy failed — please select & copy manually.");
    }
  };

  return (
    <div
      data-testid="admin-smart-composer"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onCancel}
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FBF9F6] rounded-3xl max-w-md w-full p-7 shadow-2xl max-h-[85vh] overflow-y-auto"
      >
        <div className="eyebrow">
          {isZone ? "Zone" : "Device"} · {isEdit ? "edit" : "add"}
        </div>
        <h2 className="mt-2 font-serif text-2xl text-[#171717]">
          {isZone
            ? isEdit ? "Edit zone" : "Add a new zone"
            : isEdit ? "Edit device" : "Pair a new device"}
        </h2>

        {pairToken ? (
          <div data-testid="admin-smart-composer-pair-token" className="mt-5 space-y-4">
            <p className="text-sm text-[#525252]">
              Device gently created. Show this <strong>pair token</strong> to the
              device once — it lets it identify itself to the platform.
            </p>
            <div className="rounded-2xl bg-[#F2EFEA] p-4 break-all font-mono text-xs">
              {pairToken}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={copyToken}
                data-testid="admin-smart-composer-copy-token"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#1F3A2A]/10 text-[#1F3A2A] text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#1F3A2A]/20"
              >
                <Copy size={12} strokeWidth={1.7} />
                Copy
              </button>
              <button
                type="button"
                onClick={onSaved}
                data-testid="admin-smart-composer-done"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117]"
              >
                Done
              </button>
            </div>
          </div>
        ) : isZone ? (
          <div className="mt-5 space-y-4">
            <div>
              <label className="eyebrow">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="admin-smart-zone-name"
                placeholder="Yard"
                className="orbit-input mt-2"
              />
            </div>
            <div>
              <label className="eyebrow">Kind</label>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                data-testid="admin-smart-zone-kind"
                className="orbit-input mt-2 bg-transparent"
              >
                {ZONE_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="eyebrow">Capacity (optional)</label>
              <input
                type="number"
                min={1}
                max={200}
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                data-testid="admin-smart-zone-capacity"
                placeholder="20"
                className="orbit-input mt-2"
              />
            </div>
            <div>
              <label className="eyebrow">Assigned groups (comma-separated)</label>
              <input
                type="text"
                value={form.assigned_group_ids}
                onChange={(e) =>
                  setForm({ ...form, assigned_group_ids: e.target.value })
                }
                data-testid="admin-smart-zone-groups"
                placeholder="Sunflower Room, Maple Room"
                className="orbit-input mt-2"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                data-testid="admin-smart-composer-cancel"
                className="btn-ghost !py-2 !px-5 !text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-smart-composer-save"
                className="btn-primary !py-2 !px-5 !text-xs"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div>
              <label className="eyebrow">Label</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="admin-smart-device-label"
                placeholder="Front-room iPad"
                className="orbit-input mt-2"
              />
            </div>
            <div>
              <label className="eyebrow">Kind</label>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                data-testid="admin-smart-device-kind"
                className="orbit-input mt-2 bg-transparent"
              >
                {DEVICE_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="eyebrow">Zone (optional)</label>
              <select
                value={form.zone_id}
                onChange={(e) => setForm({ ...form, zone_id: e.target.value })}
                data-testid="admin-smart-device-zone"
                className="orbit-input mt-2 bg-transparent"
              >
                <option value="">— None —</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>{z.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="eyebrow">Group (optional)</label>
              <input
                type="text"
                value={form.group_id}
                onChange={(e) => setForm({ ...form, group_id: e.target.value })}
                data-testid="admin-smart-device-group"
                placeholder="Sunflower Room"
                className="orbit-input mt-2"
              />
            </div>
            <div>
              <label className="eyebrow">Note</label>
              <input
                type="text"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="admin-smart-device-note"
                placeholder="Mounted near south door"
                className="orbit-input mt-2"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                data-testid="admin-smart-composer-cancel"
                className="btn-ghost !py-2 !px-5 !text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-smart-composer-save"
                className="btn-primary !py-2 !px-5 !text-xs"
              >
                {saving ? "Saving…" : isEdit ? "Save" : "Pair"}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
